import { useState, useEffect, useCallback, useRef } from "react";
import api from "../../utils/api";
import MainCard from "components/MainCard";
import PageHeader from "components/PageHeader";
import {
  Avatar, Box, Chip, CircularProgress,
  Dialog, DialogContent, DialogTitle, IconButton,
  InputAdornment, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography, Paper,
  MenuItem, Select, FormControl, InputLabel,
} from "@mui/material";
import {
  SearchOutlined, UserOutlined,
  CheckCircleOutlined, CloseCircleOutlined,
  MobileOutlined, MailOutlined, ReloadOutlined,
  EnvironmentOutlined, HistoryOutlined,
} from "@ant-design/icons";
import { stopRowClickPropagation } from "components/table/ClickableTableRow";
import TableStatusChip from "components/table/TableStatusChip";
import {
  mutedOutlineChipSx,
  tableIconActiveSx,
  tableIconMutedSx,
  verifiedChipSx
} from "components/table/tableChipStyles";
import { useToast } from "contexts/ToastContext";

function VerifiedChip({ verified }) {
  return (
    <Chip
      icon={verified ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
      label={verified ? "Verified" : "Unverified"}
      size="small"
      variant="outlined"
      sx={verifiedChipSx(verified)}
    />
  );
}

function LoginTypeChip({ type }) {
  return (
    <Chip
      icon={type === "email" ? <MailOutlined /> : <MobileOutlined />}
      label={type === "email" ? "Email" : "Mobile"}
      size="small"
      variant="outlined"
      sx={mutedOutlineChipSx}
    />
  );
}

function parseCustomerListResponse(res) {
  const body = res?.data;
  const pagination = body?.pagination;
  const raw = body?.data ?? body;

  const customers = Array.isArray(raw)
    ? raw
    : raw?.customers ?? raw?.customer_list ?? raw?.list ?? [];

  const list = Array.isArray(customers) ? customers : [];

  const total =
    pagination?.total ??
    pagination?.total_count ??
    pagination?.totalCount ??
    (Array.isArray(raw) ? undefined : raw?.total) ??
    body?.total ??
    list.length;

  const currentPage = Number(pagination?.page) || 1;
  const totalPages = Number(pagination?.total_pages) || 1;

  return {
    customers: list,
    total: Number(total) || 0,
    currentPage,
    totalPages
  };
}

const PAGE_LIMIT = 8;

function formatLocationTime(val) {
  if (!val) return "—";
  const d = new Date(val);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
}

function formatCoord(val) {
  if (val == null || val === "") return "—";
  const n = Number(val);
  return Number.isNaN(n) ? String(val) : n.toFixed(6);
}

function LocationHistoryDialog({ open, onClose, deviceId, customerName }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!open || !deviceId) return;
    setLoading(true);
    setError(null);
    setHistory([]);

    api.get(`/mobile/device/location/history/${deviceId}`)
      .then((res) => {
        const body = res.data;
        if (body?.status === false) {
          throw new Error(body?.message || "Failed to load location history");
        }
        const list = Array.isArray(body?.data) ? body.data : [];
        setHistory(list);
      })
      .catch((err) => {
        setError(err.response?.data?.message || err?.message || "Failed to load location history");
      })
      .finally(() => setLoading(false));
  }, [open, deviceId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" gap={1}>
            <HistoryOutlined style={{ color: "#1677ff" }} />
            <Typography variant="h6">Location History</Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {customerName}
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block">
          Device: {deviceId}
        </Typography>
        {!loading && !error && history.length > 0 && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            {history.length} record{history.length !== 1 ? "s" : ""}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>

        {/* Loading */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {/* Error */}
        {!loading && error && (
          <Box sx={{ textAlign: "center", p: 4 }}>
            <Typography color="error" variant="body2">{error}</Typography>
          </Box>
        )}

        {/* Empty */}
        {!loading && !error && history.length === 0 && (
          <Box sx={{ textAlign: "center", p: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No location history found for this device
            </Typography>
          </Box>
        )}

        {!loading && !error && history.length > 0 && (
          <TableContainer
            sx={{
              maxHeight: 400,
              overflowY: "auto",
              borderTop: "1px solid",
              borderColor: "divider"
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.50" }}>
                  <TableCell sx={{ fontWeight: 700, width: 44 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 150 }}>Date & time</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Latitude</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Longitude</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Map</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((h, idx) => {
                  const lat = h.latitude ?? h.lat;
                  const lng = h.longitude ?? h.lng;
                  const recordedAt = h.createdAt ?? h.created_at;
                  const hasCoords = lat != null && lng != null && lat !== "" && lng !== "";

                  return (
                    <TableRow key={h.id || idx} hover>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {idx + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {formatLocationTime(recordedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                          {formatCoord(lat)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                          {formatCoord(lng)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {hasCoords ? (
                          <Tooltip title={`${lat}, ${lng}`}>
                            <IconButton
                              size="small"
                              sx={tableIconActiveSx}
                              onClick={() =>
                                window.open(
                                  `https://maps.google.com/?q=${lat},${lng}`,
                                  "_blank",
                                  "noopener,noreferrer"
                                )
                              }
                            >
                              <EnvironmentOutlined style={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function CustomerList() {
  const { showToast, showApiError } = useToast();

  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [verifyFilter, setVerifyFilter] = useState("all");
  const nextPageRef = useRef(1);
  const scrollRef = useRef(null);
  const loadMoreSentinelRef = useRef(null);
  const loadMoreInFlightRef = useRef(false);
  const loadMoreRef = useRef(() => {});

  const [histDialog, setHistDialog] = useState({
    open: false, deviceId: null, customerName: "",
  });

  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const buildListParams = useCallback(
    (pageNum) => {
      const params = { page: pageNum, limit: PAGE_LIMIT };
      if (searchDebounced) params.search = searchDebounced;
      if (statusFilter === "active") params.is_active = true;
      if (statusFilter === "inactive") params.is_active = false;
      if (verifyFilter === "verified") params.is_verified = true;
      if (verifyFilter === "unverified") params.is_verified = false;
      return params;
    },
    [searchDebounced, statusFilter, verifyFilter]
  );

  const fetchCustomersPage = useCallback(
    async (pageNum) => {
      const res = await api.get("/mobile/customer/list", {
        params: buildListParams(pageNum)
      });
      return parseCustomerListResponse(res);
    },
    [buildListParams]
  );

  const loadInitial = useCallback(async () => {
    setLoading(true);
    setError(null);
    setHasMore(true);
    nextPageRef.current = 1;
    try {
      const result = await fetchCustomersPage(1);
      setCustomers(result.customers);
      setTotal(result.total);
      setHasMore(result.currentPage < result.totalPages);
      nextPageRef.current = 2;
    } catch (err) {
      console.error("[CustomerList] load error:", err);
      setCustomers([]);
      setTotal(0);
      setHasMore(false);
      setError(
        err.response?.status === 401
          ? "Unauthorized — please log in again."
          : err.response?.data?.message || "Failed to load customers."
      );
    } finally {
      setLoading(false);
    }
  }, [fetchCustomersPage]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore || loadMoreInFlightRef.current) return;

    const pageNum = nextPageRef.current;
    loadMoreInFlightRef.current = true;
    setLoadingMore(true);
    try {
      const result = await fetchCustomersPage(pageNum);
      setCustomers((prev) => {
        const seen = new Set(prev.map((c) => c.id));
        const added = result.customers.filter((c) => !seen.has(c.id));
        return [...prev, ...added];
      });
      setTotal(result.total);
      setHasMore(result.currentPage < result.totalPages);
      nextPageRef.current = pageNum + 1;
    } catch (err) {
      showApiError(err, "Failed to load more customers");
    } finally {
      loadMoreInFlightRef.current = false;
      setLoadingMore(false);
    }
  }, [loading, loadingMore, hasMore, fetchCustomersPage, showApiError]);

  loadMoreRef.current = loadMore;

  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = loadMoreSentinelRef.current;
    if (!root || !sentinel || !hasMore || loading || loadingMore) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMoreRef.current();
      },
      { root, rootMargin: "40px", threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore]);

  const handleToggleStatus = async (c) => {
    setTogglingId(c.id);
    try {
      await api.put(`/mobile/customer/manage/status/${c.id}`);
      setCustomers((prev) =>
        prev.map((x) =>
          x.id === c.id ? { ...x, is_active: !x.is_active } : x
        )
      );
    } catch (err) {
      showApiError(err, "Status update failed");
    } finally {
      setTogglingId(null);
    }
  };

  const getDevice = (c) =>
    c.CustomerDeviceMaps?.[0] ?? c.CustomerDeviceMap ?? null;


  const getLatLng = (c) => {
    const dev = getDevice(c);
    if (!dev) return null;
    const cfg = dev.ConfigModel ?? dev.config_model ?? null;
    if (!cfg) return null;
    if (cfg.latitude && cfg.longitude)
      return { lat: cfg.latitude, lng: cfg.longitude };
    return null;
  };

  const fmtDate = (val) =>
    val
      ? new Date(val).toLocaleDateString("en-IN", {
          day: "2-digit", month: "short", year: "numeric",
        })
      : "—";

  const fmtOrderCount = (val, verified) => {
    if (!verified) return "—";
    if (val == null || val === "") return "—";
    const n = Number(val);
    return Number.isNaN(n) ? "—" : String(n);
  };

  return (
    <Box>

      <PageHeader
        title="Customers"
        subtitle="View and manage registered customers, verification, and activity."
        count={loading ? "…" : total}
        action={
          <Tooltip title="Refresh">
            <IconButton onClick={loadInitial} disabled={loading}>
              <ReloadOutlined />
            </IconButton>
          </Tooltip>
        }
      />

      {/* Filters */}
      <MainCard sx={{ mb: 2, border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" }} content={false}>
        <Box sx={{ p: 2.5, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Search by name or mobile…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchOutlined /></InputAdornment>
              ),
              sx: { borderRadius: 2 }
            }}
            sx={{ minWidth: 280, flex: 1, maxWidth: 420 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="customer-status-label">Status</InputLabel>
            <Select
              labelId="customer-status-label"
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="customer-verify-label">Verification</InputLabel>
            <Select
              labelId="customer-verify-label"
              value={verifyFilter}
              label="Verification"
              onChange={(e) => setVerifyFilter(e.target.value)}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="verified">Verified</MenuItem>
              <MenuItem value="unverified">Unverified</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </MainCard>

      {/* Table */}
      <MainCard contentSX={{ p: 0 }}>

        {loading && customers.length === 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && customers.length === 0 && (
          <Box sx={{ textAlign: "center", p: 6 }}>
            <Typography color="error" variant="body1" sx={{ mb: 1 }}>{error}</Typography>
            <Typography variant="caption" color="primary"
              sx={{ cursor: "pointer" }} onClick={loadInitial}>
              Try again
            </Typography>
          </Box>
        )}

        {!loading && !error && customers.length === 0 && (
          <Box sx={{ textAlign: "center", p: 6 }}>
            <Typography variant="h6">No customers found</Typography>
            {(search || statusFilter !== "all" || verifyFilter !== "all") && (
              <Typography variant="caption" color="text.secondary">
                Try clearing your filters
              </Typography>
            )}
          </Box>
        )}

        {!loading && !error && customers.length > 0 && (
          <TableContainer
            ref={scrollRef}
            component={Paper}
            elevation={0}
            sx={{
              display: "block",
              maxHeight: { xs: "55vh", md: 520 },
              minHeight: 360,
              overflowX: "auto",
              overflowY: "scroll",
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(31, 91, 64, 0.45) rgba(31, 91, 64, 0.08)",
              WebkitOverflowScrolling: "touch",
              "&::-webkit-scrollbar": { width: 10, height: 10 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(31, 91, 64, 0.4)",
                borderRadius: 8,
                border: "2px solid rgba(31, 91, 64, 0.06)"
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "rgba(31, 91, 64, 0.08)",
                borderRadius: 8
              }
            }}
          >
            <Table stickyHeader size="small" sx={{ minWidth: 1080 }}>
              <TableHead>
                <TableRow>
                  {/* customers table */}
                  <TableCell sx={{ fontWeight: 700, minWidth: 190 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 140 }}>Mobile</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Login type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Verified</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 110 }}>Registered</TableCell>
                  {/* config_models: latitude, longitude (current location) */}
                  <TableCell sx={{ fontWeight: 700, minWidth: 80 }} align="center">
                    Location
                  </TableCell>
                  {/* location_histories via device_id */}
                  <TableCell sx={{ fontWeight: 700, minWidth: 80 }} align="center">
                    Loc. History
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 90 }} align="center">
                    Total Orders
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 90 }} align="center">
                    Cancelled
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {customers.map((c) => {
                  const device  = getDevice(c);
                  const deviceId = device?.device_id ?? null;
                  const latLng   = getLatLng(c);

                  return (
                    <TableRow key={c.id} hover>

                      {/* DB: customers.name */}
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar sx={{
                            width: 36, height: 36,
                            bgcolor: "primary.lighter",
                            fontSize: 14, fontWeight: 600,
                          }}>
                            {(c.name || c.mobile_number || "C")[0].toUpperCase()}
                          </Avatar>
                          <Typography variant="body2" fontWeight={600}>
                            {c.name || "—"}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* DB: customers.mobile_number */}
                      <TableCell>
                        <Typography variant="body2">{c.mobile_number || "—"}</Typography>
                      </TableCell>

                      {/* DB: customers.login_type */}
                      <TableCell>
                        <LoginTypeChip type={c.login_type} />
                      </TableCell>

                      {/* DB: customers.is_verified */}
                      <TableCell>
                        <VerifiedChip verified={!!c.is_verified} />
                      </TableCell>

                      {/* DB: customers.is_active */}
                      <TableCell>
                        <Tooltip title="Click to toggle">
                          <span>
                            <TableStatusChip
                              active={!!c.is_active}
                              loading={togglingId === c.id}
                              onClick={stopRowClickPropagation(() => handleToggleStatus(c))}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>

                      {/* DB: customers.createdAt */}
                      <TableCell>
                        <Typography variant="body2">{fmtDate(c.createdAt)}</Typography>
                      </TableCell>

                      {/* DB: config_models.latitude + longitude (current location)
                          joined via: customer → customer_device_maps → config_models
                          Click → opens Google Maps */}
                      <TableCell align="center">
                        <Tooltip
                          title={
                            latLng
                              ? `Lat: ${latLng.lat}, Lng: ${latLng.lng} — Open in Maps`
                              : "No location data"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={!latLng}
                              sx={latLng ? tableIconActiveSx : tableIconMutedSx}
                              onClick={stopRowClickPropagation(() =>
                                window.open(
                                  `https://maps.google.com/?q=${latLng.lat},${latLng.lng}`,
                                  "_blank"
                                )
                              )}
                            >
                              <EnvironmentOutlined style={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>

                      {/* location_histories table via device_id
                          Click → opens dialog → GET /mobile/device/location/history/:device_id */}
                      <TableCell align="center">
                        <Tooltip
                          title={
                            deviceId
                              ? "View location history"
                              : "No device linked to this customer"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={!deviceId}
                              sx={deviceId ? tableIconActiveSx : tableIconMutedSx}
                              onClick={stopRowClickPropagation(() =>
                                setHistDialog({
                                  open: true,
                                  deviceId,
                                  customerName: c.name || c.mobile_number || "Customer",
                                })
                              )}
                            >
                              <HistoryOutlined style={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>

                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color={c.is_verified ? "text.primary" : "text.disabled"}
                        >
                          {fmtOrderCount(c.total_order, c.is_verified)}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{
                            color: !c.is_verified
                              ? "text.disabled"
                              : Number(c.cancelled_order) > 0
                                ? "error.main"
                                : "text.primary"
                          }}
                        >
                          {fmtOrderCount(c.cancelled_order, c.is_verified)}
                        </Typography>
                      </TableCell>

                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell
                    ref={loadMoreSentinelRef}
                    colSpan={10}
                    align="center"
                    sx={{ py: 1.5, border: 0 }}
                  >
                    {loadingMore ? (
                      <CircularProgress size={24} />
                    ) : !hasMore ? (
                      <Typography variant="caption" color="text.secondary">
                        All {total} customers loaded
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Scroll for more
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </MainCard>

      {/* Location History  */}
      <LocationHistoryDialog
        open={histDialog.open}
        deviceId={histDialog.deviceId}
        customerName={histDialog.customerName}
        onClose={() =>
          setHistDialog({ open: false, deviceId: null, customerName: "" })
        }
      />
    </Box>
  );
}